import { Client } from "..";
import { Shake } from "../utils/Shake";
import { LevelType, Level } from "../utils/TimelineTypes";
import { GameEvents } from "../utils/Types";
import { LevelDescriptor } from "./windows/LevelDescriptor";

class TimelineSquare extends Phaser.GameObjects.Container {
  private squareGraphics: Phaser.GameObjects.Graphics;
  private icon?: Phaser.GameObjects.Image;
  private level: Level;
  private isCompleted: boolean = false;
  private isCurrentLevel: boolean = false;
  private activeTweens: Phaser.Tweens.Tween[] = [];
  private timeline: Timeline;

  constructor(
    scene: Phaser.Scene,
    level: Level,
    x: number,
    y: number,
    size: number,
    timeline: Timeline,
  ) {
    super(scene, x, y);

    this.level = level;
    this.timeline = timeline;

    this.setSize(size, size);

    // Create graphics for the square
    this.squareGraphics = new Phaser.GameObjects.Graphics(scene);
    this.add(this.squareGraphics);

    // Create icon
    if (level.icon && level.icon !== "") {
      this.icon = scene.add.image(0, 0, level.icon);
      this.icon.setOrigin(0.5, 0.5);
      this.icon.setScale(
        (size * 0.7) / Math.max(this.icon.width, this.icon.height),
      );
      this.add(this.icon);
    }

    this.redraw();
    this.setupInteractivity();
  }

  private setupInteractivity(): void {
    // Make the entire square interactive with a larger hit area for easier hovering
    const hitAreaSize = Math.max(this.width, this.height) * 1.2; // 20% larger than the square

    // Adjust the interactive area to match the visual
    const offsetX = hitAreaSize / 4; // Shift right to align horizontally
    const offsetY = hitAreaSize / 4; // Shift down to align vertically
    this.setInteractive(
      new Phaser.Geom.Rectangle(
        -hitAreaSize / 2 + offsetX,
        -hitAreaSize / 2 + offsetY,
        hitAreaSize,
        hitAreaSize,
      ),
      Phaser.Geom.Rectangle.Contains,
    );

    // Setup hover events
    this.on("pointerover", this.onHoverStart, this);
    this.on("pointerout", this.onHoverEnd, this);
  }

  private onHoverStart(): void {
    // Only show descriptor if this square is visible (alpha > 0.5)
    if (this.alpha > 0.5) {
      const pointer = this.scene.input.activePointer;
      this.timeline.showLevelDescriptor(this.level, pointer.x, pointer.y);
    }
  }

  private onHoverEnd(): void {
    this.timeline.hideLevelDescriptor();
  }

  public updateState(isCurrentLevel: boolean, isCompleted: boolean): void {
    this.isCurrentLevel = isCurrentLevel;
    this.isCompleted = isCompleted;
    this.redraw();
  }

  private redraw(): void {
    this.squareGraphics.clear();

    // Choose color based on level type and completion status
    const color = this.getLevelColor(this.level.levelType, this.isCompleted);

    // Draw filled rounded rectangle (centered on container)
    this.squareGraphics.fillStyle(color);
    this.squareGraphics.fillRoundedRect(
      -this.width / 2,
      -this.height / 2,
      this.width,
      this.height,
      3,
    );

    // Draw border
    if (this.isCurrentLevel) {
      this.squareGraphics.lineStyle(4, 0xfff57a); // Thick yellow border for current level
    } else if (this.isCompleted) {
      this.squareGraphics.lineStyle(2, 0x604c7d); // Light gray border for completed levels
    } else {
      this.squareGraphics.lineStyle(2, 0xffffff); // White border for future levels
    }
    this.squareGraphics.strokeRoundedRect(
      -this.width / 2,
      -this.height / 2,
      this.width,
      this.height,
      3,
    );

    // Update icon tint if it exists
    if (this.icon) {
      if (this.isCompleted) {
        this.icon.setTint(0x604c7d);
      } else {
        this.icon.clearTint();
      }
    }
  }

  private getLevelColor(
    levelType: LevelType,
    isCompleted: boolean = false,
  ): number {
    if (isCompleted) {
      return 0x432547;
    }

    switch (levelType) {
      case LevelType.STORE:
        return 0x0b9d5f;
      case LevelType.OPPONENT:
        return 0xfa6735;
      case LevelType.BOSS:
        return 0xe82337;
      case LevelType.TUTORIAL_OPPONENT:
        return 0xfa6735;
      default:
        return 0x432547;
    }
  }

  public override destroy(): void {
    // Clean up any active tweens
    this.activeTweens.forEach((tween) => {
      if (tween && tween.isActive()) {
        tween.stop();
      }
    });
    this.activeTweens = [];
    super.destroy();
  }
}

export class Timeline extends Phaser.GameObjects.Container {
  private graphics: Phaser.GameObjects.Graphics;
  private squareSize: number = 50;
  private spacing: number = 10;
  private timelineSquares: TimelineSquare[] = [];
  private lastCurrentLevel: number = -1;
  private activeTweens: Phaser.Tweens.Tween[] = [];
  private levelDescriptor: LevelDescriptor;

  constructor(scene: Phaser.Scene) {
    super(scene);
    this.graphics = new Phaser.GameObjects.Graphics(scene);
    this.add(this.graphics);

    // Create level descriptor
    this.levelDescriptor = new LevelDescriptor(scene);
    scene.add.existing(this.levelDescriptor);

    // Listen for timeline visibility changes
    Client.game.events.on(
      GameEvents.TIMELINE_VISIBILITY_UPDATED,
      this.onTimelineVisibilityUpdated,
      this,
    );

    Client.game.events.on(GameEvents.LEVEL_UPDATED, this.onLevelUpdated, this);
    Client.game.events.on(
      GameEvents.LEVEL_ACTION_TRIGGERED,
      this.onLevelActionTriggered,
      this,
    );
  }

  public Resize(
    width: number = this.width,
    height: number = this.height,
  ): void {
    this.setSize(width, height);

    const timeline = Client.state.timeline || [];
    if (timeline.length === 0) {
      return;
    }

    const currentLevel = Client.state.level || 1;

    // Draw gradient line that matches visible range only
    if (timeline.length > 1) {
      this.drawGradientLine(timeline, currentLevel, height);
    }

    // Check if this is just a level change (timeline squares already exist)
    if (
      this.timelineSquares.length > 0 &&
      this.lastCurrentLevel !== currentLevel
    ) {
      // Trigger smooth animation instead of recreating everything
      this.triggerLevelChange(currentLevel);
      return;
    }

    // Clear existing timeline squares only if we need to recreate them
    this.timelineSquares.forEach((square) => square.destroy());
    this.timelineSquares = [];

    // Calculate vertical positions for squares with current level centered
    const positions = this.calculateCenteredVerticalPositions(
      timeline,
      currentLevel,
      height,
    );

    // Create timeline squares for all levels
    for (let i = 0; i < timeline.length && i < positions.length; i++) {
      const level = timeline[i];
      const levelIndex = i;
      const pos = positions[i];
      const isCurrentLevel = levelIndex + 1 === currentLevel;
      const isCompleted = levelIndex + 1 < currentLevel; // Levels before current are completed

      // Create TimelineSquare
      const timelineSquare = new TimelineSquare(
        this.scene,
        level,
        pos.x,
        pos.y,
        this.squareSize,
        this,
      );

      // Set initial state
      timelineSquare.updateState(isCurrentLevel, isCompleted);

      // Scale current level to 10% bigger
      if (isCurrentLevel) {
        timelineSquare.setScale(1.1);
      }

      // Start with alpha 0 - will be set correctly by updateVisibility
      timelineSquare.setAlpha(0);

      this.add(timelineSquare);
      this.timelineSquares.push(timelineSquare);
    }

    // Update visibility after all squares are created and positioned
    this.updateVisibility();

    this.lastCurrentLevel = currentLevel;
  }

  private calculateCenteredVerticalPositions(
    timeline: Level[],
    currentLevel: number,
    containerHeight: number,
  ): { x: number; y: number }[] {
    const positions: { x: number; y: number }[] = [];
    const centerX = this.squareSize / 2 + 10; // Center X position for all squares
    const centerY = containerHeight / 2; // Center Y position of container

    // Find the index of the current level in the timeline array (currentLevel is 1-based)
    const currentLevelIndex = currentLevel - 1;

    // Calculate positions with current level at center Y
    for (let i = 0; i < timeline.length; i++) {
      // Calculate offset from current level
      const offsetFromCurrent = i - currentLevelIndex;
      const y = centerY + offsetFromCurrent * (this.squareSize + this.spacing);
      positions.push({ x: centerX, y: y });
    }

    return positions;
  }

  public triggerLevelChange(newLevel: number): void {
    if (this.lastCurrentLevel !== newLevel && this.timelineSquares.length > 0) {
      this.animateToNewLevel(this.lastCurrentLevel, newLevel);
      this.lastCurrentLevel = newLevel;
      // Update visibility for the new level
      this.updateVisibility();
    }
  }

  public updateTimelineVisibility(): void {
    // Update visibility when timeline visibility changes
    this.updateVisibility();
  }

  public showLevelDescriptor(
    level: Level,
    mouseX: number,
    mouseY: number,
  ): void {
    this.levelDescriptor.showForLevel(level, mouseX, mouseY);
  }

  public hideLevelDescriptor(): void {
    this.levelDescriptor.hideDescriptor();
  }

  private animateToNewLevel(oldLevel: number, newLevel: number): void {
    // Clean up any existing tweens
    this.activeTweens.forEach((tween) => {
      if (tween && tween.isActive()) {
        tween.stop();
      }
    });
    this.activeTweens = [];

    const timeline = Client.state.timeline || [];
    if (timeline.length === 0) return;

    // Calculate how much to move the timeline to keep new current level centered
    const levelDifference = newLevel - oldLevel;
    const moveDistance = -levelDifference * (this.squareSize + this.spacing);

    // Animate all squares by the same offset to create smooth flow
    for (let i = 0; i < this.timelineSquares.length; i++) {
      const square = this.timelineSquares[i];
      const isNewCurrentLevel = i + 1 === newLevel;
      const isOldCurrentLevel = i + 1 === oldLevel;
      const isCompleted = i + 1 < newLevel;

      // Update the square's state
      square.updateState(isNewCurrentLevel, isCompleted);

      // Animate position - move all squares by the same distance for smooth flow
      const positionTween = this.scene.tweens.add({
        targets: square,
        y: square.y + moveDistance,
        duration: 400, // 25% faster than default 500ms
        ease: "Power2.easeInOut",
      });
      this.activeTweens.push(positionTween);

      // Animate scale changes
      let targetScale = 1.0;
      if (isNewCurrentLevel) {
        targetScale = 1.1; // 10% bigger for current level
      }

      if (isOldCurrentLevel || isNewCurrentLevel) {
        const scaleTween = this.scene.tweens.add({
          targets: square,
          scaleX: targetScale,
          scaleY: targetScale,
          duration: 400, // 25% faster than default 500ms
          ease: "Power2.easeInOut",
        });
        this.activeTweens.push(scaleTween);
      }
    }

    // Add a tween that continuously updates visibility during animation
    const visibilityUpdateTween = this.scene.tweens.add({
      targets: {}, // Dummy target
      duration: 400, // Same duration as position tweens
      ease: "Power2.easeInOut",
      onUpdate: () => {
        // Update visibility during animation
        this.updateVisibility();
      },
    });
    this.activeTweens.push(visibilityUpdateTween);
  }

  private drawGradientLine(
    _timeline: Level[],
    _currentLevel: number,
    _height: number,
  ): void {
    const centerX = this.squareSize / 2 + 10; // Center of timeline column
    const levelSpacing = this.squareSize + this.spacing;
    const centerY = this.height / 2;
    const timelineVisibility = Client.state.timelineVisibility || 2;

    // Calculate Y positions for visible range based on level indexes
    const topLevelY = centerY - 2 * levelSpacing; // 2 levels behind
    const bottomLevelY = centerY + timelineVisibility * levelSpacing; // X levels ahead

    const visibleHeight = bottomLevelY - topLevelY;
    const lineWidth = 6;
    const grayColor = 0x604c7d;
    const lineX = centerX - lineWidth / 2;

    // Draw main line covering only visible squares
    this.graphics.clear();
    this.graphics.fillStyle(grayColor);
    this.graphics.fillRect(lineX, topLevelY, lineWidth, visibleHeight);

    // Add top gradient (transparent to solid)
    this.graphics.fillGradientStyle(
      grayColor,
      grayColor,
      grayColor,
      grayColor,
      0,
      0,
      1,
      1,
    );
    this.graphics.fillRect(
      lineX,
      topLevelY - this.squareSize,
      lineWidth,
      this.squareSize,
    );

    // Add bottom gradient (solid to transparent)
    this.graphics.fillGradientStyle(
      grayColor,
      grayColor,
      grayColor,
      grayColor,
      1,
      1,
      0,
      0,
    );
    this.graphics.fillRect(lineX, bottomLevelY, lineWidth, this.squareSize);
  }

  private updateVisibility(): void {
    // Smoothly transition visibility based on level indexes
    const currentLevel = Client.state.level || 1;
    const timelineVisibility = Client.state.timelineVisibility || 2;

    this.timelineSquares.forEach((square, index) => {
      const levelIndex = index + 1; // Convert to 1-based level number

      // Check if level should be visible based on range
      const levelsBehind = currentLevel - levelIndex;
      const levelsAhead = levelIndex - currentLevel;

      const shouldBeVisible =
        (levelsBehind <= 2 && levelsBehind >= 0) || // 2 behind + current
        (levelsAhead <= timelineVisibility && levelsAhead > 0); // X ahead

      const currentAlpha = square.alpha;
      const targetAlpha = shouldBeVisible ? 1.0 : 0.0;

      // Only tween if alpha needs to change
      if (Math.abs(currentAlpha - targetAlpha) > 0.01) {
        const alphaTween = this.scene.tweens.add({
          targets: square,
          alpha: targetAlpha,
          duration: 300, // Smooth transition
          ease: "Power2.easeInOut",
        });
        this.activeTweens.push(alphaTween);
      }
    });
  }

  private onTimelineVisibilityUpdated = (): void => {
    this.updateTimelineVisibility();
  };

  private onLevelUpdated(): void {
    this.triggerLevelChange(Client.state.level);
  }

  private onLevelActionTriggered(): void {
    this.timelineSquares[this.lastCurrentLevel - 1]
      ?.getData("shakeTween")
      ?.complete();
    const tween = Shake(
      this.scene,
      this.timelineSquares[this.lastCurrentLevel - 1],
      { duration: 250, magnitude: 8, rotate: true, restore: true },
    );
    this.timelineSquares[this.lastCurrentLevel - 1]?.setData(
      "shakeTween",
      tween,
    );
  }

  public override destroy(): void {
    // Clean up event listeners
    Client.game.events.off(
      GameEvents.TIMELINE_VISIBILITY_UPDATED,
      this.onTimelineVisibilityUpdated,
      this,
    );

    // Clean up any active tweens
    this.activeTweens.forEach((tween) => {
      if (tween && tween.isActive()) {
        tween.stop();
      }
    });
    this.activeTweens = [];

    // Clean up level descriptor
    if (this.levelDescriptor) {
      this.levelDescriptor.destroy();
    }

    super.destroy();
  }
}
